import React, { FC } from 'react';
import { Button, Skeleton } from 'antd';
import { useHistory } from 'react-router-dom';
import { formatTimeAgo } from 'common/helpers/date.helper';
import { ArrowsInSimple, ArrowsOutSimple } from 'common/svg';
import { DashboardCard } from 'common/components/DashboardCard';
import { ERoutesFranchiseePrivate, RoutesCirPrefix } from 'common/models/routesModel';
import { useAppSelector } from 'app/store/store.hooks';
import {
  ETrackStoreNotificationType,
  ITrackStoreNotification,
  trackStoreNotificationIcons,
} from 'entities/TrackStore/TrackStore.models';
import { updateStorageStoreCode } from 'entities/Store/Store.helper';

const getTitle = (notification: ITrackStoreNotification) => {
  if (notification.type === ETrackStoreNotificationType.Announcement) {
    return 'Announcement';
  } else if (notification.type === ETrackStoreNotificationType.FoodForThought) {
    return 'Food for Thought';
  } else {
    return notification.storeCode;
  }
};

interface IComponentProps {
  storeCode?: string | number;
  expandable?: {
    isExpanded: boolean;
    onIsExpandedChange: (isVisible: boolean) => void;
  };
}

export const TrackStoreNotifications: FC<IComponentProps> = ({ storeCode, expandable }) => {
  const { trackStoreNotificationCollection, trackStoreNotificationCollectionLoading } = useAppSelector(
    (state) => state.trackStore
  );

  const history = useHistory();

  const notifications = trackStoreNotificationCollection?.data ?? [];
  const newNotification = notifications.filter((item) => item.isNew);
  const showNewNotificationsCounter = !!newNotification.length;

  const handleIsExpandedChange = () => {
    if (expandable) {
      expandable.onIsExpandedChange(!expandable.isExpanded);
    }
  };

  const goToGeneratePo = () => {
    if (storeCode) {
      updateStorageStoreCode(storeCode.toString());
    }

    history.push(`${RoutesCirPrefix}${ERoutesFranchiseePrivate.GeneratePO}`);
  };

  return (
    <DashboardCard
      title={
        <span>
          Notifications
          {showNewNotificationsCounter && (
            <span className="ml-3 flex-align-center flex-justify-center ff-regular fs-xxs tsrt-notifications-new-items-counter">
              {newNotification.length}
            </span>
          )}
        </span>
      }
      variant="notifications"
      extra={
        expandable ? (
          <div className="cursor-pointer tsrt-notifications-expand" onClick={handleIsExpandedChange}>
            {expandable.isExpanded ? <ArrowsInSimple /> : <ArrowsOutSimple />}
          </div>
        ) : undefined
      }
      className="tsrt-notifications"
    >
      {trackStoreNotificationCollectionLoading ? (
        <div className="p-5">
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        </div>
      ) : (
        <div className="p-5 height-full flex flex__column">
          <div
            className="scrollbar-y tsrt-notifications-scroll"
            style={{ maxHeight: expandable?.isExpanded ? 'auto' : 200 }}
          >
            {notifications.map((item) => {
              const timeAgo = formatTimeAgo(item.createdAt);

              return (
                <div key={item.id} className="mb-2 p-3 flex tsrt-notification">
                  <div
                    className={`mr-3 flex flex-align-center flex-justify-center tsrt-notification-icon ${item.type}`}
                  >
                    {trackStoreNotificationIcons[item.type]}
                  </div>

                  <div className="flex__item_1">
                    <div className="mb-1 flex flex-align-center flex-justify-between">
                      <span className="ff-medium fs-xxs">{getTitle(item)}</span>

                      <div className="flex flex-align-center">
                        {item.isNew && <div className="mr-2 tsrt-notification-new-indicator" />}
                        <span className="ff-regular fs-xxxs color-gray-1">{timeAgo}</span>
                      </div>
                    </div>
                    <div className="mb-1 ff-regular fs-xxs color-gray-1">{item.message}</div>

                    {item.type === ETrackStoreNotificationType.PoNotYetPlaced && (
                      <div className="mt-3">
                        <Button className="btn-primary" onClick={goToGeneratePo}>
                          Generate PO
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DashboardCard>
  );
};
